import { api } from "@/lib/api";

/**
 * The phone's socket closing under the console, reported so the log can say what closed it.
 *
 * On AWS (2026-09-24) several reps' phones were closed from their side once a minute and back two
 * seconds later. The switch can say only that the close came from the browser's end ("Client
 * sent close request"); everything that tells one cause from another is here in the page: the
 * close code, whether a close frame was exchanged, whether the tab was in the background and for
 * how long, whether the browser had frozen the page, whether the switch had gone quiet first. So
 * each unexpected close is described and sent to core-api (`POST /phone/socket-drops`), which
 * writes it to its log next to who it was.
 *
 * Only a close the console did not ask for gets here: a disconnect the phone itself made retires
 * the client before its close arrives (`isCurrent()` in softphone-context). A report that cannot
 * be sent — the network the socket went down with — is kept, and sent with the next drop, when the
 * phone reconnects, or when the browser is back online.
 */

/** What the page itself went through, which no single event carries. */
export interface PageState {
  /** When the tab was last hidden, or null while it is visible. Epoch ms. */
  hiddenSince: number | null;
  /** The Page Lifecycle `freeze` and `resume` events: the browser stopping the page outright. */
  lastFreezeAt: number | null;
  lastResumeAt: number | null;
}

const page: PageState = { hiddenSince: null, lastFreezeAt: null, lastResumeAt: null };
let watching = false;

/**
 * Start following the page's visibility and lifecycle. Once per page; a no-op outside one.
 *
 * Also sends whatever is queued when the browser says it is online again: the phone can reconnect
 * before the page's own requests work (seen with Chrome's offline mode, which lets a WebSocket
 * through), and then nothing else would bring the queued reports round until the next drop.
 */
export function watchPage(): void {
  if (watching || typeof document === "undefined") return;
  watching = true;
  window.addEventListener("online", () => void socketDrops.flush());
  if (document.visibilityState === "hidden") page.hiddenSince = Date.now();
  document.addEventListener("visibilitychange", () => {
    page.hiddenSince = document.visibilityState === "hidden" ? Date.now() : null;
  });
  document.addEventListener("freeze", () => {
    page.lastFreezeAt = Date.now();
  });
  document.addEventListener("resume", () => {
    page.lastResumeAt = Date.now();
  });
}

/** The vendored client's `disconnected` payload. */
export interface SocketClose {
  code?: number;
  reason?: string;
  wasClean?: boolean;
  /** Epoch ms the socket opened, and last received anything; null when it never did. */
  openedAt?: number | null;
  lastMessageAt?: number | null;
}

/** What the page can say about itself at the moment of the close. */
export interface Surroundings {
  now: number;
  visibility?: string;
  focused?: boolean;
  online?: boolean;
  connection?: string;
  page: PageState;
}

/** One drop as `POST /phone/socket-drops` takes it. Null is "the browser did not say". */
export interface SocketDrop {
  at: string;
  code: number;
  reason: string | null;
  was_clean: boolean | null;
  visibility: string | null;
  hidden_ms: number | null;
  focused: boolean | null;
  online: boolean | null;
  open_ms: number | null;
  since_message_ms: number | null;
  since_freeze_ms: number | null;
  since_resume_ms: number | null;
  connection: string | null;
}

/** 1005 is the code a browser reports for a close that carried none. */
const NO_STATUS = 1005;

export function describeDrop(close: SocketClose, here: Surroundings): SocketDrop {
  const since = (t: number | null | undefined) =>
    typeof t === "number" ? Math.max(0, Math.round(here.now - t)) : null;
  const code =
    typeof close.code === "number" && close.code >= 0 && close.code <= 4999 ? close.code : NO_STATUS;
  return {
    at: new Date(here.now).toISOString(),
    code,
    reason: close.reason ? close.reason.slice(0, 123) : null,
    was_clean: close.wasClean ?? null,
    visibility: here.visibility ?? null,
    hidden_ms: here.visibility === "hidden" ? since(here.page.hiddenSince) : null,
    focused: here.focused ?? null,
    online: here.online ?? null,
    open_ms: since(close.openedAt),
    since_message_ms: since(close.lastMessageAt),
    since_freeze_ms: since(here.page.lastFreezeAt),
    since_resume_ms: since(here.page.lastResumeAt),
    connection: here.connection ? here.connection.slice(0, 16) : null,
  };
}

/** The page as it is right now. */
export function surroundings(now = Date.now()): Surroundings {
  const nav: (Navigator & { connection?: { effectiveType?: string } }) | undefined =
    typeof navigator === "undefined" ? undefined : navigator;
  const doc = typeof document === "undefined" ? undefined : document;
  return {
    now,
    visibility: doc?.visibilityState,
    focused: typeof doc?.hasFocus === "function" ? doc.hasFocus() : undefined,
    online: nav?.onLine,
    connection: nav?.connection?.effectiveType,
    page: { ...page },
  };
}

/** As many as core-api takes in one report; older ones give way to newer. */
export const MAX_QUEUED = 20;

/**
 * Holds drops until core-api has them. One send at a time; whatever was not sent goes with the next
 * drop, the next reconnect or the browser's `online` event — never retried in a loop of its own.
 */
export class DropReporter {
  private queued: SocketDrop[] = [];
  private sending = false;

  constructor(private readonly post: (drops: SocketDrop[]) => Promise<unknown>) {}

  report(drop: SocketDrop): void {
    this.queued = [...this.queued, drop].slice(-MAX_QUEUED);
    void this.flush();
  }

  async flush(): Promise<void> {
    if (this.sending || this.queued.length === 0) return;
    this.sending = true;
    const batch = this.queued;
    try {
      await this.post(batch);
      this.queued = this.queued.filter((drop) => !batch.includes(drop));
    } catch {
      // Kept for the next try: the network may have gone down with the socket.
    } finally {
      this.sending = false;
    }
  }

  pending(): readonly SocketDrop[] {
    return this.queued;
  }
}

export const socketDrops = new DropReporter((drops) =>
  api.post("/phone/socket-drops", { drops }),
);
